import { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Alert, Snackbar, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Tooltip, Tabs, Tab, CircularProgress
} from '@mui/material';
import { Receipt, Send, Delete, Refresh, PictureAsPdf } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function RecibosVenta() {
  const [recibos, setRecibos] = useState([]);
  const [tab, setTab] = useState(0); // 0=pendientes, 1=todos
  const [loading, setLoading] = useState(true);
  const [procesando, setProcesando] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => { cargar(); }, [tab]);

  const cargar = async () => {
    setLoading(true);
    try {
      const url = tab === 0
        ? `${API_URL}/api/recibos/pendientes`
        : `${API_URL}/api/recibos`;
      const res = await axios.get(url, { headers: headers() });
      setRecibos(res.data?.data || []);
    } catch { setRecibos([]); }
    setLoading(false);
  };

  const facturarRecibo = async (recibo) => {
    if (!window.confirm(`¿Convertir recibo ${recibo.numero} en factura electrónica SRI?`)) return;
    setProcesando(recibo.id);
    try {
      // Emitir factura con los datos del recibo
      const payload = {
        cliente: recibo.cliente,
        detalles: recibo.detalles.map(d => ({
          codigo: d.codigo, nombre: d.nombre, cantidad: d.cantidad,
          precio: d.precio, iva: d.iva, descuento: d.descuento || 0,
        })),
        totales: {
          subtotalSinImpuestos: recibo.totales.subtotal,
          subtotal15: recibo.detalles.filter(d => d.iva !== '0%').reduce((s, d) => s + d.cantidad * d.precio, 0),
          subtotal0: recibo.detalles.filter(d => d.iva === '0%').reduce((s, d) => s + d.cantidad * d.precio, 0),
          totalDescuento: recibo.totales.descuento || 0,
          iva15: recibo.totales.iva,
          valorPagar: recibo.totales.total,
        },
        pago: { forma: recibo.forma_pago || '01', valor: recibo.totales.total },
        skipInventario: true, // Ya se descontó al emitir el recibo
      };

      const resFactura = await axios.post(`${API_URL}/api/factura`, payload, { headers: headers() });

      if (resFactura.data.success !== false) {
        // Marcar recibo como facturado
        await axios.put(`${API_URL}/api/recibos/${recibo.id}/facturar`, {
          facturaId: resFactura.data.claveAcceso,
          facturaClave: resFactura.data.claveAcceso,
          secuencial: resFactura.data.secuencial,
        }, { headers: headers() });

        setSnackbar({ open: true, message: `✅ Recibo ${recibo.numero} facturado (${resFactura.data.secuencial || 'OK'})`, severity: 'success' });
        cargar();
      } else {
        setSnackbar({ open: true, message: `❌ ${resFactura.data.error || 'Error al facturar'}`, severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: `❌ ${err.response?.data?.error || err.message}`, severity: 'error' });
    }
    setProcesando(null);
  };

  const anularRecibo = async (recibo) => {
    if (!window.confirm(`¿Anular recibo ${recibo.numero}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/recibos/${recibo.id}`, { headers: headers() });
      setSnackbar({ open: true, message: 'Recibo anulado', severity: 'success' });
      cargar();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Error', severity: 'error' });
    }
  };

  const fmt = (v) => `$${parseFloat(v || 0).toFixed(2)}`;
  const fmtFecha = (f) => f ? new Date(f).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const chipEstado = (estado) => {
    const colores = { PENDIENTE: 'warning', FACTURADO: 'success', ANULADO: 'error' };
    return <Chip label={estado} size="small" color={colores[estado] || 'default'} />;
  };

  const reimprimirRecibo = (recibo) => {
    const configImp = JSON.parse(localStorage.getItem('config_impresora') || '{}');
    const tipo = configImp.tipo_ticket || '58mm';
    const ancho = tipo === '58mm' ? 32 : 48;
    const linea = '-'.repeat(ancho);
    const centrar = (t) => { const p = Math.max(0, Math.floor((ancho - t.length) / 2)); return ' '.repeat(p) + t; };
    const derecha = (lab, val) => `${lab}${' '.repeat(Math.max(1, ancho - lab.length - val.length))}${val}`;

    const items = (recibo.detalles || []).map(d => {
      const nombre = d.nombre.substring(0, ancho - 10);
      return `${d.cantidad}x ${nombre}\n${derecha('', '$' + (d.cantidad * d.precio).toFixed(2))}`;
    }).join('\n');

    const ticket = [
      centrar(configImp.nombre_empresa_ticket || 'EMPRESA'),
      centrar('*** RECIBO DE VENTA ***'),
      centrar(`No: ${recibo.numero}`),
      linea,
      centrar(new Date(recibo.fecha).toLocaleString('es-EC')),
      `Cliente: ${recibo.cliente?.nombre || 'CONSUMIDOR FINAL'}`,
      recibo.cliente?.ruc !== '9999999999999' ? `CI/RUC: ${recibo.cliente?.ruc}` : '',
      linea,
      items,
      linea,
      derecha('SUBTOTAL:', `$${(recibo.totales?.subtotal || 0).toFixed(2)}`),
      derecha('IVA:', `$${(recibo.totales?.iva || 0).toFixed(2)}`),
      linea,
      derecha('TOTAL:', `$${(recibo.totales?.total || 0).toFixed(2)}`),
      linea,
      centrar('** NO ES FACTURA **'),
      centrar('** REIMPRESION **'),
      centrar(configImp.mensaje_pie_ticket || 'Gracias por su compra'),
      '',
    ].filter(Boolean).join('\n');

    const ventana = window.open('', '_blank', `width=${tipo === '58mm' ? 220 : 302},height=600`);
    if (!ventana) return;
    ventana.document.write(`<!DOCTYPE html><html><head><title>Recibo</title>
      <style>@page{margin:0;size:${tipo} auto;}*{margin:0;padding:0;}
      body{font-family:'Courier New',monospace;font-size:${tipo === '58mm' ? '9px' : '11px'};padding:${configImp.margen_ticket || 3}px;width:${tipo === '58mm' ? '48mm' : '72mm'};}
      pre{white-space:pre-wrap;word-wrap:break-word;}</style></head>
      <body><pre>${ticket}</pre>
      <script>window.onload=function(){window.print();setTimeout(function(){window.close()},1500);}</script>
    </body></html>`);
    ventana.document.close();
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5" fontWeight={700}>🧾 Recibos de Venta</Typography>
          <Button variant="outlined" startIcon={<Refresh />} onClick={cargar}>Actualizar</Button>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Recibos emitidos sin factura electrónica. Puede convertirlos a factura SRI cuando el cliente lo solicite.
        </Alert>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={`Pendientes de Factura`} />
          <Tab label="Todos los Recibos" />
        </Tabs>

        {loading ? <Box textAlign="center" py={4}><CircularProgress /></Box> : recibos.length === 0 ? (
          <Alert severity="info">{tab === 0 ? 'No hay recibos pendientes de factura.' : 'No hay recibos registrados.'}</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Nº Recibo</strong></TableCell>
                  <TableCell><strong>Fecha</strong></TableCell>
                  <TableCell><strong>Cliente</strong></TableCell>
                  <TableCell align="center"><strong>Items</strong></TableCell>
                  <TableCell align="right"><strong>Total</strong></TableCell>
                  <TableCell align="center"><strong>Estado</strong></TableCell>
                  <TableCell align="center"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recibos.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell><Typography fontFamily="monospace" fontWeight={600}>{r.numero}</Typography></TableCell>
                    <TableCell>{fmtFecha(r.fecha)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.cliente?.nombre}</Typography>
                      {r.cliente?.ruc !== '9999999999999' && <Typography variant="caption" color="text.secondary">{r.cliente?.ruc}</Typography>}
                    </TableCell>
                    <TableCell align="center">{r.detalles?.length || 0}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(r.totales?.total)}</TableCell>
                    <TableCell align="center">{chipEstado(r.estado)}</TableCell>
                    <TableCell align="center">
                      {r.estado === 'PENDIENTE' && (
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Reimprimir recibo">
                            <IconButton size="small" color="primary" onClick={() => reimprimirRecibo(r)}>
                              <Receipt fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Facturar al SRI">
                            <IconButton size="small" color="success" onClick={() => facturarRecibo(r)} disabled={procesando === r.id}>
                              {procesando === r.id ? <CircularProgress size={16} /> : <Send fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Anular">
                            <IconButton size="small" color="error" onClick={() => anularRecibo(r)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                      {r.estado === 'FACTURADO' && (
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Reimprimir">
                            <IconButton size="small" color="primary" onClick={() => reimprimirRecibo(r)}>
                              <Receipt fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Chip label={`Fact: ${r.factura_secuencial || '✓'}`} size="small" color="success" variant="outlined" />
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Resumen */}
        {recibos.length > 0 && tab === 0 && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: '#fff3e0' }}>
            <Typography variant="body2">
              <strong>{recibos.length}</strong> recibos pendientes — Total: <strong>{fmt(recibos.reduce((s, r) => s + (r.totales?.total || 0), 0))}</strong>
            </Typography>
          </Paper>
        )}
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
